/**
 * The two screens an emailed link lands on.
 *
 * `${PUBLIC_ORIGIN}/verify?t=...` and `${PUBLIC_ORIGIN}/reset?t=...` have been in every mail this
 * project queues since Phase 1 and there was no client path that could consume either. Not a
 * broken one: none. `App.tsx` initialised its route from a literal, `net/api.ts` had no `verify`
 * method and no `reset` method, and the only query parameter anything read was `?purchase`.
 *
 * **THIS IS NOT A ROUTER AND THAT IS A RULING.** DESIGN-DELTAS 1t.2. `assets.not_found_handling` is
 * `single-page-application`, so `/verify` already serves `index.html` and boots the app; all that
 * was ever missing was one read of `window.location.pathname`. A real router is a refactor of the
 * shell, the nav rail and thirteen screens to buy two landing pages, and Phase 12 made the same
 * call for the Stripe return with a query parameter. The two literals are read ONCE, at mount, in
 * `App.tsx`, and the path is replaced the moment the screen is done with it, which is the same
 * discipline `?purchase` already follows: a reload must not be a second attempt and a shared URL
 * must not carry somebody's live token.
 *
 * The mail keeps PATHS rather than query parameters because a link a person reads in an inbox is
 * part of the product. `ashport.city/verify?t=...` says what it does; `ashport.city/?verify=...`
 * says nothing and looks like a tracker.
 */

import { useEffect, useState } from 'react'
import { A } from '../art'
import { api, ApiError } from '../net/api'
import { Btn, Panel } from '../ui/Kit'
import { Note } from './Gate'

export type LetterKind = 'verify' | 'reset'

/** Both screens sit on the key art, which is what the app already holds during boot. */
function Frame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background: `var(--coal) url(${A('brand.loading_key_art_wide')}) center / cover no-repeat`,
      }}
    >
      <Panel style={{ width: '100%', maxWidth: 430, padding: 24 }}>
        <div
          className="t-display"
          style={{ fontSize: 30, letterSpacing: '.06em', color: 'var(--gold-hot)', lineHeight: 1 }}
        >
          {title}
        </div>
        <div style={{ marginTop: 14 }}>{children}</div>
      </Panel>
    </div>
  )
}

/**
 * Confirm the address.
 *
 * It fires ONCE on mount and the token is single use, so a second fire would consume nothing and
 * report an expired link over a successful one. React mounts effects twice under StrictMode in
 * development, which is exactly the shape that produces that bug, so the guard is a ref rather
 * than a dependency array.
 */
function Verify({ token, onDone }: { token: string; onDone: () => void }) {
  const [state, setState] = useState<'working' | 'done' | 'bad'>('working')
  const [problem, setProblem] = useState('')

  useEffect(() => {
    let dead = false
    void api
      .verify(token)
      .then(() => {
        if (!dead) setState('done')
      })
      .catch((e: unknown) => {
        if (dead) return
        setProblem(e instanceof ApiError ? e.message : 'That link did not go through.')
        setState('bad')
      })
    return () => {
      dead = true
    }
  }, [token])

  return (
    <Frame title={state === 'done' ? 'CONFIRMED' : state === 'bad' ? 'NO GOOD' : 'CHECKING'}>
      {state === 'working' && (
        <div style={{ fontSize: 12.5, color: 'var(--ink-mid)', lineHeight: 1.6 }}>
          One moment.
        </div>
      )}
      {state === 'done' && (
        <>
          <div style={{ fontSize: 12.5, color: 'var(--ink-mid)', lineHeight: 1.6, marginBottom: 16 }}>
            That address is on the account for good now. It is how you get back in if you lose the
            password, and it is where the notice goes when the Era ends.
          </div>
          <Btn v="gold" full onClick={onDone}>
            INTO THE CITY
          </Btn>
        </>
      )}
      {state === 'bad' && (
        <>
          <Note tone="bad">{problem}</Note>
          <div style={{ fontSize: 12, color: 'var(--ink-dim)', lineHeight: 1.6, marginBottom: 16 }}>
            A confirmation link is good for a day and works once. Sign in and ask for another from
            the line at the top of the screen.
          </div>
          <Btn full onClick={onDone}>
            BACK TO THE DOOR
          </Btn>
        </>
      )}
    </Frame>
  )
}

/**
 * Set a new password from the link.
 *
 * The server revokes every other session on success, so this ends with the player signed out
 * everywhere including here. That is the point of a reset and the screen says so before the tap
 * rather than after it.
 */
function Reset({ token, onDone }: { token: string; onDone: () => void }) {
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const mismatch = again !== '' && password !== again

  const submit = async () => {
    if (busy) return
    if (password !== again) {
      setProblem('Those two are not the same.')
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      await api.resetConfirm(token, password)
      setDone(true)
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : 'That did not go through.')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <Frame title="CHANGED">
        <div style={{ fontSize: 12.5, color: 'var(--ink-mid)', lineHeight: 1.6, marginBottom: 16 }}>
          The password is the new one and every other session is closed. If somebody else was in
          this account, they are not any more.
        </div>
        <Btn v="gold" full onClick={onDone}>
          SIGN IN
        </Btn>
      </Frame>
    )
  }

  return (
    <Frame title="NEW PASSWORD">
      <div style={{ fontSize: 12.5, color: 'var(--ink-mid)', lineHeight: 1.6, marginBottom: 16 }}>
        Twelve characters or more. Setting it signs you out of every other browser and device.
      </div>
      {(['new', 'again'] as const).map((k) => (
        <label key={k} style={{ display: 'block', marginBottom: 12 }}>
          <span
            className="t-label"
            style={{ display: 'block', color: 'var(--ink-dim)', marginBottom: 5, letterSpacing: '.14em' }}
          >
            {k === 'new' ? 'Password' : 'Again'}
          </span>
          <input
            type="password"
            autoComplete="new-password"
            value={k === 'new' ? password : again}
            onChange={(e) => (k === 'new' ? setPassword(e.target.value) : setAgain(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit()
            }}
            style={{
              width: '100%',
              background: 'var(--char)',
              border: `1px solid ${k === 'again' && mismatch ? 'var(--arrest)' : 'var(--line)'}`,
              borderRadius: 2,
              color: 'var(--ink)',
              font: 'inherit',
              fontSize: 13,
              padding: '9px 10px',
              outline: 'none',
            }}
          />
        </label>
      ))}
      {problem !== null && <Note tone="bad">{problem}</Note>}
      <Btn v="gold" full disabled={busy || password === '' || mismatch} onClick={submit}>
        {busy ? 'HOLD ON' : 'SET IT'}
      </Btn>
    </Frame>
  )
}

export function Letter({
  kind,
  token,
  onDone,
}: {
  kind: LetterKind
  token: string
  onDone: () => void
}) {
  /*
   * A link with no token at all is its own state, and it is the one that would have happened to
   * every real player: `render()` in `server/src/lib/mail.ts` builds the URL from `p.token` and
   * `auth.ts` sent `link`, so every mail this project queued carried a bare `?t=`. That is fixed
   * at the source; this stays because a truncated link in a mail client is ordinary.
   */
  if (token === '') {
    return (
      <Frame title="HALF A LINK">
        <Note tone="bad">That link arrived without its token. Mail clients cut long lines.</Note>
        <div style={{ fontSize: 12, color: 'var(--ink-dim)', lineHeight: 1.6, marginBottom: 16 }}>
          Copy the whole address out of the mail, or ask for another one.
        </div>
        <Btn full onClick={onDone}>
          BACK TO THE DOOR
        </Btn>
      </Frame>
    )
  }
  return kind === 'verify' ? (
    <Verify token={token} onDone={onDone} />
  ) : (
    <Reset token={token} onDone={onDone} />
  )
}
